import React from 'react';
import { API_BASE_URL } from '../config.js';
import InstitutionHeader from './reportcard/InstitutionHeader.jsx';
import StudentInformation from './reportcard/StudentInformation.jsx';
import AcademicPerformanceTable from './reportcard/AcademicPerformanceTable.jsx';
import ResultSummary from './reportcard/ResultSummary.jsx';
import CoScholasticSection from './reportcard/CoScholasticSection.jsx';
import SkillsAssessment from './reportcard/SkillsAssessment.jsx';
import AttendanceSection from './reportcard/AttendanceSection.jsx';
import RemarksSection from './reportcard/RemarksSection.jsx';
import PromotionSection from './reportcard/PromotionSection.jsx';
import GradingScale from './reportcard/GradingScale.jsx';
import SignatureFooter from './reportcard/SignatureFooter.jsx';
import CustomSection from './reportcard/CustomSection.jsx';

const DEFAULT_AVATAR = 'data:image/svg+xml,' + encodeURIComponent(`
  <svg xmlns="http://www.w3.org/2000/svg" width="100" height="120" viewBox="0 0 100 120">
    <rect width="100" height="120" rx="8" fill="#f1f5f9" stroke="#cbd5e1" stroke-width="2"/>
    <circle cx="50" cy="42" r="22" fill="#94a3b8"/>
    <ellipse cx="50" cy="100" rx="34" ry="24" fill="#94a3b8"/>
  </svg>
`);

export const detectTemplateKey = (cfg) => {
  if (!cfg) return 'classic_academic';
  if (cfg.templateKey) return cfg.templateKey;
  const title = (cfg.header?.title || '').toUpperCase();
  const layout = cfg.header?.layout || '';
  if (title.includes('KINDERGARTEN')) return 'kindergarten_progress';
  if (title.includes('PRIMARY')) return 'primary_progress';
  if (title.includes('ACADEMIC TRANSCRIPT') || layout === 'banner') return 'modern_school';
  if (title.includes('TERM-BASED') || title.includes('PERIODIC')) return 'term_based_progress';
  if (title.includes('ANNUAL') || title.includes('CUMULATIVE')) return 'annual_progress';
  if (title.includes('STATE BOARD')) return 'state_board_record';
  if (title.includes('STATEMENT OF MARKS')) return 'pu_college';
  if (title.includes('PROGRESS REPORT')) return 'classic_academic';
  return 'classic_academic';
};

export const PRESET_DEFAULTS = {
  classic_academic: {
    templateKey: 'classic_academic',
    header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'PROGRESS REPORT', layout: 'centered' },
    studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
    columns: [
      { key: 'subjectName', label: 'Subject', dataSource: 'subject.name', visible: true, order: 1, width: '35%', alignment: 'left' },
      { key: 'maxMarks', label: 'Max Marks', dataSource: 'assessment.maxMarks', visible: true, order: 2, width: '15%', alignment: 'center' },
      { key: 'obtainedMarks', label: 'Obtained', dataSource: 'assessment.obtainedMarks', visible: true, order: 3, width: '15%', alignment: 'center' },
      { key: 'percentage', label: 'Percentage', dataSource: 'calculated.percentage', visible: true, order: 4, width: '15%', alignment: 'center' },
      { key: 'grade', label: 'Grade', dataSource: 'calculated.grade', visible: true, order: 5, width: '20%', alignment: 'center' }
    ],
    additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
    customSections: [],
    footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
    style: { primaryColor: '#1e3a8a', accentColor: '#f59e0b', fontFamily: 'Serif', borderStyle: 'gold_crest', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
  },
  modern_school: {
    templateKey: 'modern_school',
    header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'ACADEMIC TRANSCRIPT', layout: 'banner' },
    studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
    columns: [
      { key: 'subjectName', label: 'Subject', dataSource: 'subject.name', visible: true, order: 1, width: '28%', alignment: 'left' },
      { key: 'fa1', label: 'FA1', dataSource: 'assessment.fa1', examMapping: 'PT-1', visible: true, order: 2, width: '11%', alignment: 'center' },
      { key: 'fa2', label: 'FA2', dataSource: 'assessment.fa2', examMapping: 'PT-2', visible: true, order: 3, width: '11%', alignment: 'center' },
      { key: 'sa1', label: 'SA1', dataSource: 'assessment.sa1', examMapping: 'Mid Term', visible: true, order: 4, width: '11%', alignment: 'center' },
      { key: 'sa2', label: 'SA2', dataSource: 'assessment.sa2', examMapping: 'Annual', visible: true, order: 5, width: '11%', alignment: 'center' },
      { key: 'obtainedMarks', label: 'Total', dataSource: 'result.obtainedMarks', visible: true, order: 6, width: '14%', alignment: 'center' },
      { key: 'grade', label: 'Grade', dataSource: 'calculated.grade', visible: true, order: 7, width: '14%', alignment: 'center' }
    ],
    additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
    customSections: [],
    footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
    style: { primaryColor: '#2563eb', accentColor: '#10b981', fontFamily: 'Inter', borderStyle: 'clean_modern', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
  },
  primary_progress: {
    templateKey: 'primary_progress',
    header: { showLogo: true, showSchoolName: true, showAffiliationNo: false, showAddress: true, showContact: true, title: 'PRIMARY PROGRESS CARD', layout: 'logo_left' },
    studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
    columns: [
      { key: 'subjectName', label: 'Learning Domain / Subject', dataSource: 'subject.name', visible: true, order: 1, width: '45%', alignment: 'left' },
      { key: 'obtainedMarks', label: 'Marks Obtained', dataSource: 'result.obtainedMarks', visible: true, order: 2, width: '20%', alignment: 'center' },
      { key: 'grade', label: 'Performance Grade', dataSource: 'calculated.grade', visible: true, order: 3, width: '35%', alignment: 'center' }
    ],
    additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: true, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
    customSections: [],
    footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
    style: { primaryColor: '#0d9488', accentColor: '#f43f5e', fontFamily: 'Outfit', borderStyle: 'rounded_playful', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
  },
  kindergarten_progress: {
    templateKey: 'kindergarten_progress',
    header: { showLogo: true, showSchoolName: true, showAffiliationNo: false, showAddress: true, showContact: true, title: 'KINDERGARTEN DEVELOPMENTAL PROGRESS', layout: 'centered' },
    studentInfo: { showPhoto: true, showStudentId: false, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'two_column' },
    columns: [
      { key: 'subjectName', label: 'Development Domain', dataSource: 'subject.name', visible: true, order: 1, width: '50%', alignment: 'left' },
      { key: 'grade', label: 'Grade / Level', dataSource: 'calculated.grade', visible: true, order: 2, width: '50%', alignment: 'center' }
    ],
    domainSkillsList: [
      { category: 'Language & Communication Skills', rating: 'A+' },
      { category: 'Numeracy & Quantitative Thinking', rating: 'A' },
      { category: 'Gross & Fine Motor Skills', rating: 'A+' },
      { category: 'Social & Emotional Development', rating: 'A' },
      { category: 'Art, Music & Creativity', rating: 'A+' }
    ],
    additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: true, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: false, showQR: true },
    customSections: [],
    footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
    style: { primaryColor: '#8b5cf6', accentColor: '#ec4899', fontFamily: 'Outfit', borderStyle: 'pastel_border', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
  },
  term_based_progress: {
    templateKey: 'term_based_progress',
    header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'TERM-BASED PROGRESS REPORT', layout: 'logo_left' },
    studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
    columnGroups: [
      {
        groupName: 'TERM 1',
        columns: [
          { key: 't1_pt', label: 'PT (10)', dataSource: 'assessment.periodicTest', examMapping: 'PT-1', visible: true, width: '10%', alignment: 'center' },
          { key: 't1_nb', label: 'NB (5)', dataSource: 'assessment.notebook', visible: true, width: '8%', alignment: 'center' },
          { key: 't1_sea', label: 'SEA (5)', dataSource: 'assessment.subjectEnrichment', visible: true, width: '8%', alignment: 'center' },
          { key: 't1_hy', label: 'Half Yearly (80)', dataSource: 'assessment.halfYearly', examMapping: 'Mid Term', visible: true, width: '14%', alignment: 'center' },
          { key: 't1_tot', label: 'Total (100)', dataSource: 'calculated.total', visible: true, width: '12%', alignment: 'center' }
        ]
      },
      {
        groupName: 'TERM 2',
        columns: [
          { key: 't2_pt', label: 'PT (10)', dataSource: 'assessment.periodicTest', examMapping: 'PT-2', visible: true, width: '10%', alignment: 'center' },
          { key: 't2_nb', label: 'NB (5)', dataSource: 'assessment.notebook', visible: true, width: '8%', alignment: 'center' },
          { key: 't2_sea', label: 'SEA (5)', dataSource: 'assessment.subjectEnrichment', visible: true, width: '8%', alignment: 'center' },
          { key: 't2_ye', label: 'Yearly (80)', dataSource: 'assessment.yearly', examMapping: 'Final Exam', visible: true, width: '14%', alignment: 'center' },
          { key: 't2_tot', label: 'Total (100)', dataSource: 'calculated.total', visible: true, width: '12%', alignment: 'center' }
        ]
      }
    ],
    additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
    customSections: [],
    footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
    style: { primaryColor: '#15803d', accentColor: '#0284c7', fontFamily: 'Inter', borderStyle: 'clean_modern', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
  },
  annual_progress: {
    templateKey: 'annual_progress',
    header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'ANNUAL CUMULATIVE PROGRESS REPORT', layout: 'centered' },
    studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
    columns: [
      { key: 'subjectName', label: 'Subject', dataSource: 'subject.name', visible: true, order: 1, width: '30%', alignment: 'left' },
      { key: 'term1Total', label: 'Term 1 (100)', dataSource: 'assessment.halfYearly', visible: true, order: 2, width: '16%', alignment: 'center' },
      { key: 'term2Total', label: 'Term 2 (100)', dataSource: 'assessment.yearly', visible: true, order: 3, width: '16%', alignment: 'center' },
      { key: 'grandTotal', label: 'Grand Total (200)', dataSource: 'calculated.total', visible: true, order: 4, width: '18%', alignment: 'center' },
      { key: 'grade', label: 'Final Grade', dataSource: 'calculated.grade', visible: true, order: 5, width: '20%', alignment: 'center' }
    ],
    additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
    customSections: [],
    footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
    style: { primaryColor: '#1e3a8a', accentColor: '#059669', fontFamily: 'Inter', borderStyle: 'clean_modern', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
  },
  state_board_record: {
    templateKey: 'state_board_record',
    header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'STATE BOARD ANNUAL RECORD SHEET', layout: 'logo_left' },
    studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showScholarNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
    columns: [
      { key: 'subjectName', label: 'Subject', dataSource: 'subject.name', visible: true, order: 1, width: '30%', alignment: 'left' },
      { key: 'monthlyEval', label: 'Monthly Eval (20)', dataSource: 'assessment.periodicTest', visible: true, order: 2, width: '15%', alignment: 'center' },
      { key: 'halfYearly', label: 'Half Yearly (30)', dataSource: 'assessment.halfYearly', visible: true, order: 3, width: '15%', alignment: 'center' },
      { key: 'yearlyEval', label: 'Yearly Eval (50)', dataSource: 'assessment.yearly', visible: true, order: 4, width: '15%', alignment: 'center' },
      { key: 'annualTotal', label: 'Annual Total (100)', dataSource: 'calculated.total', visible: true, order: 5, width: '15%', alignment: 'center' },
      { key: 'grade', label: 'Grade', dataSource: 'calculated.grade', visible: true, order: 6, width: '10%', alignment: 'center' }
    ],
    additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: true, showCoCurricular: true, showPromotion: true, showGradingScale: true, showQR: true },
    customSections: [],
    footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: true },
    style: { primaryColor: '#b91c1c', accentColor: '#d97706', fontFamily: 'Serif', borderStyle: 'formal_border', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
  },
  pu_college: {
    templateKey: 'pu_college',
    header: { showLogo: true, showSchoolName: true, showAffiliationNo: true, showAddress: true, showContact: true, title: 'STATEMENT OF MARKS & CREDIT GRADES', layout: 'centered' },
    studentInfo: { showPhoto: true, showStudentId: true, showAdmissionNo: true, showRollNo: true, showClassSection: true, showDOB: true, showParentName: true, layout: 'three_column' },
    columns: [
      { key: 'subjectCode', label: 'Code', dataSource: 'subject.code', visible: true, order: 1, width: '12%', alignment: 'center' },
      { key: 'subjectName', label: 'Subject Title', dataSource: 'subject.name', visible: true, order: 2, width: '32%', alignment: 'left' },
      { key: 'theoryMarks', label: 'Theory', dataSource: 'result.theoryMarks', visible: true, order: 3, width: '12%', alignment: 'center' },
      { key: 'practicalMarks', label: 'Practical', dataSource: 'result.practicalMarks', visible: true, order: 4, width: '12%', alignment: 'center' },
      { key: 'obtainedMarks', label: 'Total Marks', dataSource: 'result.obtainedMarks', visible: true, order: 5, width: '16%', alignment: 'center' },
      { key: 'credits', label: 'Credits', dataSource: 'subject.credits', visible: true, order: 6, width: '8%', alignment: 'center' },
      { key: 'grade', label: 'Grade', dataSource: 'calculated.grade', visible: true, order: 7, width: '8%', alignment: 'center' }
    ],
    additionalSections: { showAttendance: true, showTeacherRemarks: true, showPrincipalRemarks: true, showDomainSkills: false, showBehaviour: false, showCoCurricular: false, showPromotion: true, showGradingScale: true, showQR: true },
    customSections: [],
    footer: { showTeacherSignature: true, showPrincipalSignature: true, showSchoolSeal: true, showParentSignature: false },
    style: { primaryColor: '#0f172a', accentColor: '#3b82f6', fontFamily: 'Inter', borderStyle: 'formal_border', photoPosition: 'top-right', showWatermark: true, watermarkOpacity: 0.05 }
  }
};

/**
 * MarksCardRenderer – Master Modular Report Card Renderer
 * Delegates rendering to clean, decoupled section components with multi-page A4 print support.
 */
const MarksCardRenderer = ({
  reportCard = {},
  entries = [],
  branding = {},
  institutionName = '',
  institutionAddress = '',
  id = 'marks-card-capture',
  template = 'classic_academic',
  templateConfig = null,
  gradingScale = []
}) => {
  const {
    reportCardNumber = '',
    student = {},
    academicYear = '',
    term = '',
    classTeacherRemarks = '',
    principalRemarks = ''
  } = reportCard;

  // 1. Resolve template configuration
  let cfg = null;

  // If templateConfig is explicitly passed (e.g. Designer live editor), use it directly
  if (templateConfig) {
    if (typeof templateConfig === 'string') {
      try { cfg = JSON.parse(templateConfig); } catch (e) { cfg = null; }
    } else {
      cfg = templateConfig;
    }
  }

  // If no explicit templateConfig passed:
  if (!cfg) {
    // Parse snapshot if reportCard has one
    let snapshotCfg = null;
    if (reportCard?.templateConfigSnapshot) {
      if (typeof reportCard.templateConfigSnapshot === 'string') {
        try { snapshotCfg = JSON.parse(reportCard.templateConfigSnapshot); } catch (e) { snapshotCfg = null; }
      } else {
        snapshotCfg = reportCard.templateConfigSnapshot;
      }
    }

    const snapshotKey = snapshotCfg?.templateKey || (snapshotCfg ? detectTemplateKey(snapshotCfg) : null);
    const requestedKey = template || snapshotKey || 'classic_academic';

    // If requested template matches the snapshot's key (or no specific template requested), use snapshot!
    if (snapshotCfg && (!template || template === snapshotKey)) {
      cfg = { ...snapshotCfg, templateKey: snapshotKey };
    } else if (requestedKey && PRESET_DEFAULTS[requestedKey]) {
      // User switched format (e.g. in Preview dropdown to term_based_progress, kindergarten, etc.)
      cfg = { ...PRESET_DEFAULTS[requestedKey], templateKey: requestedKey };
    } else if (snapshotCfg) {
      cfg = { ...snapshotCfg, templateKey: snapshotKey };
    } else {
      cfg = { ...PRESET_DEFAULTS.classic_academic, templateKey: 'classic_academic' };
    }
  }

  const activeKey = cfg?.templateKey || template || 'classic_academic';
  if (!cfg) {
    cfg = { ...(PRESET_DEFAULTS[activeKey] || PRESET_DEFAULTS.classic_academic), templateKey: activeKey };
  }

  const styleCfg = cfg?.style || {};
  const primaryColor = styleCfg.primaryColor || branding.primaryColor || (activeKey === 'modern_school' ? '#2563eb' : activeKey === 'primary_progress' ? '#0d9488' : activeKey === 'kindergarten_progress' ? '#8b5cf6' : activeKey === 'pu_college' ? '#0f172a' : '#1e3a8a');
  const fontFamily = styleCfg.fontFamily === 'Serif' ? 'Georgia, serif' : styleCfg.fontFamily === 'Outfit' ? 'Outfit, sans-serif' : styleCfg.fontFamily === 'Roboto' ? 'Roboto, sans-serif' : 'Inter, sans-serif';

  // Comprehensive Student Photo Resolution with config.js API_BASE_URL
  const photoSource = student.photoUrl || student.photo || student.studentPhoto || student.photo_url || student.image || student.imageUrl || '';
  const BASE_URL = API_BASE_URL || 'http://localhost:9980';
  let studentPhoto = DEFAULT_AVATAR;

  if (photoSource && photoSource !== 'null' && photoSource !== 'undefined' && photoSource.trim() !== '') {
    const cleanPhoto = photoSource.trim();
    if (cleanPhoto.startsWith('data:') || cleanPhoto.startsWith('http://') || cleanPhoto.startsWith('https://')) {
      studentPhoto = cleanPhoto;
    } else if (cleanPhoto.startsWith('/')) {
      studentPhoto = `${BASE_URL}${cleanPhoto}`;
    } else {
      studentPhoto = `${BASE_URL}/${cleanPhoto}`;
    }
  }

  const org = reportCard.organization || {};
  const orgName = institutionName || branding.organization?.orgName || org.orgName || branding.orgName || localStorage.getItem('orgName') || 'SCHOOL';
  const orgAddress = institutionAddress || branding.organization?.address || org.address || branding.address || '';

  // Merge organization data into branding for InstitutionHeader
  const mergedBranding = {
    ...branding,
    trustName: branding.trustName || org.trustName || localStorage.getItem('trustName') || '',
    affiliationNo: branding.affiliationNo || org.affiliation || '',
    email: branding.email || org.email || '',
    phone: branding.phone || org.phoneNumber || '',
    principalName: branding.principalName || org.principalName || ''
  };

  const secCfg = cfg?.additionalSections || {};
  const isKindergarten = activeKey === 'kindergarten_progress' || cfg?.category === 'Kindergarten';

  const isSecEnabled = (key, defaultVal = true) => {
    if (secCfg[key] !== undefined) return Boolean(secCfg[key]);
    return defaultVal;
  };

  const sectionsList = [
    { type: 'header', enabled: true, order: 1 },
    { type: 'studentInfo', enabled: true, order: 2 },
    { type: 'skills', enabled: isKindergarten || isSecEnabled('showDomainSkills', false), order: 3 },
    { type: 'academicTable', enabled: !isKindergarten, order: 4 },
    { type: 'resultSummary', enabled: !isKindergarten && isSecEnabled('showResultSummary', true), order: 5 },
    { type: 'coScholastic', enabled: !isKindergarten && Boolean(isSecEnabled('showPartB', false) || isSecEnabled('showCoCurricular', false)), order: 6 },
    { type: 'attendance', enabled: isSecEnabled('showAttendance', true), order: 7 },
    { type: 'remarks', enabled: isSecEnabled('showTeacherRemarks', true) || isSecEnabled('showPrincipalRemarks', true), order: 8 },
    { type: 'promotion', enabled: isSecEnabled('showPromotion', true), order: 9 },
    { type: 'customSections', enabled: true, order: 10 },
    { type: 'gradingScale', enabled: !isKindergarten && isSecEnabled('showGradingScale', true), order: 11 },
    { type: 'signatures', enabled: true, order: 12 }
  ];

  let activeSections = sectionsList;
  if (cfg.sections && Array.isArray(cfg.sections)) {
    activeSections = sectionsList.map(s => {
      const match = cfg.sections.find(cs => cs.type === s.type);
      if (match) {
        return {
          ...s,
          enabled: match.enabled !== undefined ? match.enabled : s.enabled,
          order: match.order !== undefined ? match.order : s.order
        };
      }
      return s;
    });
  }

  activeSections.sort((a, b) => (a.order || 99) - (b.order || 99));

  const renderSectionContent = (sec) => {
    if (!sec.enabled) return null;

    switch (sec.type) {
      case 'header':
        return (
          <InstitutionHeader
            headerConfig={cfg?.header}
            branding={mergedBranding}
            orgName={orgName}
            orgAddress={orgAddress}
            primaryColor={primaryColor}
            studentPhoto={studentPhoto}
            photoPos={styleCfg.photoPosition}
          />
        );
      case 'studentInfo':
        return (
          <StudentInformation
            studentConfig={cfg?.studentInfo}
            student={student}
            academicYear={academicYear}
            term={term}
            studentPhoto={studentPhoto}
            photoPos={styleCfg.photoPosition}
            primaryColor={primaryColor}
          />
        );
      case 'skills':
        if (!cfg?.domainSkillsList || cfg.domainSkillsList.length === 0) return null;
        return (
          <SkillsAssessment
            domainSkillsList={cfg?.domainSkillsList}
            primaryColor={primaryColor}
          />
        );
      case 'academicTable':
        return (
          <AcademicPerformanceTable
            columns={cfg?.columns}
            columnGroups={cfg?.columnGroups}
            entries={entries}
            primaryColor={primaryColor}
          />
        );
      case 'resultSummary':
        return (
          <ResultSummary
            reportCard={reportCard}
            primaryColor={primaryColor}
          />
        );
      case 'coScholastic':
        return (
          <CoScholasticSection
            coScholasticList={cfg?.coScholasticList}
            title={cfg?.partBTitle || 'B. Co-Scholastic Area'}
            primaryColor={primaryColor}
          />
        );
      case 'attendance':
        return (
          <AttendanceSection
            attendance={reportCard.attendance}
            primaryColor={primaryColor}
          />
        );
      case 'remarks':
        return (
          <RemarksSection
            classTeacherRemarks={classTeacherRemarks || reportCard.classTeacherRemarks}
            principalRemarks={principalRemarks || reportCard.principalRemarks}
            showTeacherRemarks={isSecEnabled('showTeacherRemarks', true)}
            showPrincipalRemarks={isSecEnabled('showPrincipalRemarks', true)}
            primaryColor={primaryColor}
          />
        );
      case 'promotion': {
        const studentClass = reportCard.student ? (reportCard.student.className || reportCard.student.academicLevel || '') : '';
        const resolveNextClass = (curClass) => {
          if (!curClass) return 'Next Higher Grade';
          const c = curClass.trim();
          if (/^lkg$/i.test(c)) return 'UKG';
          if (/^ukg$/i.test(c)) return 'Class 1';
          const match = c.match(/^(?:class|std|standard)?\s*(\d+)$/i);
          if (match) {
            const num = parseInt(match[1], 10);
            return `Class ${num + 1}`;
          }
          return `${c} - Next Grade`;
        };

        const isFromDb = Boolean(reportCard.promotedClass);
        const targetPromotionClass = reportCard.promotedClass || resolveNextClass(studentClass);
        const targetNextYear = reportCard.promotedAcademicYear || academicYear;

        return (
          <PromotionSection
            promotionClass={targetPromotionClass}
            academicYear={targetNextYear}
            status={reportCard.result || 'PASS'}
            isFromDb={isFromDb}
            primaryColor={primaryColor}
          />
        );
      }
      case 'customSections':
        return (cfg?.customSections && Array.isArray(cfg.customSections)) ? (
          <>
            {cfg.customSections.map((cSec, cIdx) => (
              <CustomSection key={cIdx} section={cSec} primaryColor={primaryColor} />
            ))}
          </>
        ) : null;
      case 'gradingScale':
        return (
          <GradingScale
            gradingScale={gradingScale}
            primaryColor={primaryColor}
          />
        );
      case 'signatures':
        return (
          <SignatureFooter
            footerConfig={cfg?.footer}
            branding={mergedBranding}
            reportCardNumber={reportCardNumber}
            showQR={isSecEnabled('showQR', true)}
          />
        );
      default:
        return null;
    }
  };

  // Refined School Watermark Resolution
  const logoSource = branding.logoUrl || branding.logo || org.logoUrl || org.logo || localStorage.getItem('schoolLogo') || '';
  let resolvedLogo = '';
  if (logoSource && logoSource !== 'null' && logoSource !== 'undefined' && logoSource.trim() !== '') {
    const cleanLogo = logoSource.trim();
    if (cleanLogo.startsWith('data:') || cleanLogo.startsWith('http://') || cleanLogo.startsWith('https://')) {
      resolvedLogo = cleanLogo;
    } else if (cleanLogo.startsWith('/')) {
      resolvedLogo = `${BASE_URL}${cleanLogo}`;
    } else {
      resolvedLogo = `${BASE_URL}/${cleanLogo}`;
    }
  }

  const watermarkOpacity = styleCfg.watermarkOpacity !== undefined ? Number(styleCfg.watermarkOpacity) : 0.035;
  const showWatermark = styleCfg.showWatermark !== false;
  const watermarkType = styleCfg.watermarkType || (resolvedLogo ? 'logo' : 'crest');
  const watermarkSize = styleCfg.watermarkSize || 360;

  return (
    <div
      id={id}
      className="marks-card-print-container"
      style={{
        width: '210mm',
        minHeight: '297mm',
        padding: '16mm 18mm',
        background: '#ffffff',
        fontFamily: fontFamily,
        color: '#1e293b',
        boxSizing: 'border-box',
        position: 'relative',
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      {/* Print Safe CSS Styles */}
      <style>{`
        @media print {
          @page { size: A4 portrait; margin: 0; }
          body { margin: 0; padding: 0; background: #fff; }
          .marks-card-print-container {
            width: 210mm !important;
            min-height: 297mm !important;
            box-shadow: none !important;
            margin: 0 !important;
            padding: 12mm 15mm !important;
          }
        }
      `}</style>

      {/* ── BACKGROUND SCHOOL LOGO WATERMARK (LIGHT COLOR) ── */}
      {showWatermark && (
        <div
          style={{
            position: 'absolute',
            top: '52%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            opacity: watermarkOpacity,
            zIndex: 0,
            pointerEvents: 'none',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            userSelect: 'none',
            width: `${watermarkSize}px`,
            height: `${watermarkSize}px`
          }}
        >
          {watermarkType === 'logo' && resolvedLogo ? (
            <img
              src={resolvedLogo}
              alt=""
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                filter: 'grayscale(100%)'
              }}
            />
          ) : watermarkType === 'monogram' ? (
            <svg viewBox="0 0 400 400" width="100%" height="100%" style={{ color: primaryColor }}>
              <circle cx="200" cy="200" r="185" fill="none" stroke="currentColor" strokeWidth="4" />
              <circle cx="200" cy="200" r="170" fill="none" stroke="currentColor" strokeWidth="1.5" strokeDasharray="6 4" />
              <circle cx="200" cy="200" r="130" fill="none" stroke="currentColor" strokeWidth="2" />
              <text x="200" y="215" textAnchor="middle" fontSize="64" fontWeight="800" fill="currentColor" fontFamily="'Times New Roman', serif">
                {(orgName || 'SCHOOL').split(' ').map(w => w[0]).join('').slice(0, 3).toUpperCase()}
              </text>
              <path id="mcCirclePath" d="M 60,200 A 140,140 0 1,1 340,200" fill="none" />
              <text fontSize="14" fontWeight="700" fill="currentColor" letterSpacing="4">
                <textPath href="#mcCirclePath" startOffset="50%" textAnchor="middle">
                  {(orgName || 'SCHOOL').toUpperCase()}
                </textPath>
              </text>
            </svg>
          ) : watermarkType === 'diagonal_text' ? (
            <div style={{
              transform: 'rotate(-30deg)',
              fontSize: '32px',
              fontWeight: '900',
              color: primaryColor,
              letterSpacing: '6px',
              textTransform: 'uppercase',
              whiteSpace: 'nowrap',
              border: `3px solid ${primaryColor}`,
              padding: '12px 32px',
              borderRadius: '8px'
            }}>
              {orgName || 'OFFICIAL REPORT CARD'}
            </div>
          ) : (
            /* Classic Academic Shield Crest */
            <svg viewBox="0 0 500 500" width="100%" height="100%" style={{ color: primaryColor }}>
              <g fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="250" cy="250" r="230" strokeDasharray="6 6" strokeWidth="2"/>
                <circle cx="250" cy="250" r="215" strokeWidth="1.5"/>
                <circle cx="250" cy="250" r="205" strokeWidth="3"/>
                
                <path d="M 150 140 Q 250 110 350 140 L 350 260 C 350 360 250 400 250 420 C 250 400 150 360 150 260 Z" strokeWidth="4" fill="none"/>
                <path d="M 165 155 Q 250 130 335 155 L 335 255 C 335 340 250 380 250 398 C 250 380 165 340 165 255 Z" strokeWidth="1.5" fill="none"/>
                
                <line x1="250" y1="130" x2="250" y2="400" strokeWidth="2"/>
                <line x1="165" y1="240" x2="335" y2="240" strokeWidth="2"/>
                
                <path d="M 185 195 Q 210 185 235 195 L 235 225 Q 210 215 185 225 Z" fill="none" strokeWidth="2"/>
                <path d="M 235 195 Q 260 185 285 195" fill="none" strokeWidth="2"/>
                <path d="M 190 202 Q 210 195 230 202 M 190 210 Q 210 203 230 210" strokeWidth="1.5"/>
                
                <path d="M 285 175 L 295 220 L 275 220 Z" strokeWidth="2"/>
                <path d="M 285 155 Q 275 168 285 175 Q 295 168 285 155 Z" strokeWidth="2"/>
                
                <circle cx="210" cy="300" r="15" strokeWidth="2"/>
                <line x1="210" y1="275" x2="210" y2="265" strokeWidth="2"/>
                <line x1="190" y1="285" x2="182" y2="278" strokeWidth="2"/>
                <line x1="230" y1="285" x2="238" y2="278" strokeWidth="2"/>
                <line x1="180" y1="300" x2="170" y2="300" strokeWidth="2"/>
                <line x1="240" y1="300" x2="250" y2="300" strokeWidth="2"/>
                
                <polygon points="290,270 294,282 306,282 296,290 300,302 290,294 280,302 284,290 274,282 286,282" strokeWidth="1.5"/>
                
                <path d="M 120 280 C 110 200 150 140 190 110" strokeWidth="2.5"/>
                <ellipse cx="140" cy="180" rx="14" ry="6" transform="rotate(-35 140 180)" strokeWidth="1.5"/>
                <ellipse cx="125" cy="220" rx="14" ry="6" transform="rotate(-15 125 220)" strokeWidth="1.5"/>
                <ellipse cx="125" cy="260" rx="14" ry="6" transform="rotate(10 125 260)" strokeWidth="1.5"/>
                <ellipse cx="140" cy="300" rx="14" ry="6" transform="rotate(35 140 300)" strokeWidth="1.5"/>
                
                <path d="M 380 280 C 390 200 350 140 310 110" strokeWidth="2.5"/>
                <ellipse cx="360" cy="180" rx="14" ry="6" transform="rotate(35 360 180)" strokeWidth="1.5"/>
                <ellipse cx="375" cy="220" rx="14" ry="6" transform="rotate(15 375 220)" strokeWidth="1.5"/>
                <ellipse cx="375" cy="260" rx="14" ry="6" transform="rotate(-10 375 260)" strokeWidth="1.5"/>
                <ellipse cx="360" cy="300" rx="14" ry="6" transform="rotate(-35 360 300)" strokeWidth="1.5"/>
              </g>
              
              <path d="M 120 420 Q 250 450 380 420 L 390 455 Q 250 485 110 455 Z" fill="none" stroke="currentColor" strokeWidth="2"/>
              <text x="250" y="445" textAnchor="middle" fontSize="16" fontWeight="bold" fill="currentColor" letterSpacing="4" fontFamily="'Times New Roman', Georgia, serif">
                EXCELLENCE • INTEGRITY • KNOWLEDGE
              </text>
            </svg>
          )}
        </div>
      )}

      {/* ── FOREGROUND MARKS CARD CONTENT ── */}
      <div style={{ position: 'relative', zIndex: 1, display: 'flex', flexDirection: 'column', width: '100%', height: '100%', flex: 1 }}>
        {activeSections.map((sec, idx) => (
          <React.Fragment key={idx}>
            {renderSectionContent(sec)}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
};

export default MarksCardRenderer;
